// @flow
import type { StoreState } from 'reducers';
import type { RequestStates } from 'types/redux';
import type { TemplateParams, HedwigActionMeta, TemplateInfo } from 'types/trident-campaigns';
import React, { Component } from 'react';
import { UCD_TARGET_AUDIENCES_ENABLED_TYPES } from 'constants/ucd';
import _ from 'lodash';

import Toastr from 'utils/toastr-util';
import { hasJustSuccess, hasJustFail } from 'utils/request-state-utils';

import {
  Form,
  Grid,
  Accordion,
  Icon,
  Popup,
  Message,
} from 'semantic-grab-ui/src';
import formComponent from 'components/decorators/form_component';
import ProgressiveContainer from 'components/container/ProgressiveContainer';

import { connect } from 'react-redux';
import { getTemplate } from 'actions/newTridentExtra';
import {
  actionTypeToTemplateType,
  tridentCommActions,
} from 'data/trident-campaigns';
import {
  getBusinessUnitTokenConstraints,
  getNewActionSendPushLanguageConstraints,
  getWhatsappTemplateConstraints,
} from './constraints';
import TemplateParamsForm from './TemplateParamsForm';
import SendPreviewMessageForm from './SendPreviewMessageForm';
import HedwigWidget from './HedwigWidget';

interface Props {
  currentNode: string; // node ID
  subActionIndex: number;
  targetUserType: string;
  actionType: string;
  actionMeta: HedwigActionMeta;
  onChange: (meta?: object) => void;
  handleInputChange: Function;
  handleControlBlur: Function;
  error: object;
  getTemplate: Function;
  templates: Record<string, TemplateInfo>;
  fetchTemplateRequest: RequestStates;
  addExtraConstraints: Function;
  clearErrors?: () => void;
  user: {
    email: string;
  };
  featureFlagConfig: Record<string, any>;
}

interface State {
  showSendMessage: boolean;
}

interface TemplateSelectData {
  templateId: string;
  templateName: string;
}

@formComponent()
export class NewActionSendPushNotification extends Component<Props, State> {
  isFetching: boolean;

  constructor(props: Props) {
    super(props);
    this.isFetching = false;
    this.state = {
      showSendMessage: false,
    };
  }

  componentDidMount() {
    const templateID = _.get(this.props.actionMeta, 'messagingPayload.templateID', '');
    if (!_.isEmpty(templateID)) { // it's an existing action
      this.isFetching = true;
      this.props.getTemplate({ templateID });
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const templateID = _.get(this.props.actionMeta, 'messagingPayload.templateID', '');

    const updatedMeta = _.cloneDeep(nextProps.actionMeta);
    if (
      hasJustSuccess(
        this.props.fetchTemplateRequest,
        nextProps.fetchTemplateRequest,
      ) && this.isFetching // there can be multiple comms action fetching template, we make sure that each action only process the request state update when it's actually fetching template
    ) {
      const template = _.get(nextProps, `templates[${templateID}]`);

      if (template) {
        this.isFetching = false;
        // store template name if not stored already
        const templateName = _.get(this.props.actionMeta, 'messagingPayload.templateName', '');
        if (_.isEmpty(templateName)) {
          _.set(updatedMeta, 'messagingPayload.templateName', template.templateName);
        }
        const paramsPayload = _.get(updatedMeta, 'messagingPayload.params');
        // null params from payload should not be handled by forcing keys from template.languageVariables
        let selectedLangs = _.keysIn(paramsPayload);
        if (paramsPayload && _.isEmpty(selectedLangs)) {
          // if no language has been selected, auto select all available languages
          selectedLangs = _.keysIn(_.get(template, 'languageVariables'));
        }
        const params: Record<string, any> = {};
        _.map(selectedLangs, (lang) => {
          const langVal = _.get(updatedMeta, `messagingPayload.params.${lang}`, {});
          if (tridentCommActions.Whatsapp.includes(nextProps.actionType)) {
            langVal.twilioStatus = _.get(template, `payloads.${lang}.payload.twilio_status`, '');
          }
          _.set(params, lang, langVal);
        });
        _.set(updatedMeta, 'messagingPayload.params', params);

        const enforceAudienceTypeValidation = _.get(nextProps.featureFlagConfig, 'enforceAudienceTypeValidation', false);
        const enableAudienceTypeValidation = _.get(nextProps.featureFlagConfig, 'enableAudienceTypeValidation', false);
        const appName = _.get(template, 'appName', '');
        const templateType = _.get(template, 'templateType', '');
        const category = _.get(template, 'category', '');
        const audienceType = _.get(template, 'audienceType', '');
        const isEmptyAudienceType = (
          UCD_TARGET_AUDIENCES_ENABLED_TYPES[appName] &&
          UCD_TARGET_AUDIENCES_ENABLED_TYPES[appName].includes(templateType) &&
          category !== 'transaction' &&
          audienceType === ''
        );

        if (enforceAudienceTypeValidation && enableAudienceTypeValidation && isEmptyAudienceType && !_.get(template, 'audienceType')) {
          // Adding the ^ to the error message to exclude the attribute name prefix, see validate.js
          _.set(updatedMeta, 'error.audienceType', '^Selected template has "Target Audience" field empty in Hedwig.');
        }

        this.props.onChange(updatedMeta);
        this.addExtraConstraints({ ...this.props, actionMeta: updatedMeta });
      }
    }

    if (
      hasJustFail(
        this.props.fetchTemplateRequest,
        nextProps.fetchTemplateRequest,
      )
    ) {
      const updatedMeta = _.cloneDeep(nextProps.actionMeta);
      const errorMessage = _.get(nextProps, 'templates.errorMessage', '');
      _.set(updatedMeta, 'error.templateID', errorMessage);
      this.isFetching = false;
      nextProps.onChange(updatedMeta);
      Toastr.error(errorMessage);
    }
  }

  handleBusinessUnitToken = (event: React.SyntheticEvent, data: { value: string }) => {
    const updatedMeta = _.cloneDeep(this.props.actionMeta);
    _.set(updatedMeta, 'messagingPayload.buToken', data.value);

    /* Empty validation */
    const errorMessage = data.value ? null : 'Business unit token is required';
    _.set(updatedMeta, 'error.buToken', errorMessage);

    this.props.onChange(updatedMeta);
  };

  handleTemplateSelect = (template: TemplateSelectData) => {
    const updatedMeta = _.cloneDeep(this.props.actionMeta);
    const oldTemplateID = _.get(this.props.actionMeta, 'messagingPayload.templateID', '');
    if (template.templateId !== oldTemplateID) {
      _.set(updatedMeta, 'messagingPayload.templateID', template.templateId);
      _.set(updatedMeta, 'messagingPayload.templateName', template.templateName);
      _.set(updatedMeta, 'messagingPayload.params', {});
      _.set(updatedMeta, 'error', {});
      this.props.onChange(updatedMeta);
      if (_.isFunction(this.props.clearErrors)) {
        this.props.clearErrors();
      }
      this.props.getTemplate({ templateID: template.templateId });
      this.isFetching = true;
    }
  };

  onTemplateParamsChange = (params: TemplateParams) => {
    const updatedMeta = _.cloneDeep(this.props.actionMeta);
    _.set(updatedMeta, 'messagingPayload.params', params);
    this.props.onChange(updatedMeta);
    this.addExtraConstraints(this.props);
  };

  addExtraConstraints(props: Props) {
    const { actionMeta, templates, currentNode, subActionIndex, actionType } = props;
    const templateID = _.get(actionMeta, 'messagingPayload.templateID', '');
    const template = _.get(templates, `${templateID}`, {});
    const addedLangs = _.keys(_.get(actionMeta, 'messagingPayload.params'));

    const extraConstraints: Record<string, any> = {};

    const isSendSMS = tridentCommActions.SMS.includes(actionType);
    const isSendWhatsapp = tridentCommActions.Whatsapp.includes(actionType);
    const isSendLine = tridentCommActions.Line.includes(actionType);

    if (isSendSMS || isSendWhatsapp || isSendLine) {
      Object.assign(extraConstraints, getBusinessUnitTokenConstraints(currentNode, subActionIndex));
    }

    addedLangs.forEach(lang => {
      if (isSendWhatsapp) {
        Object.assign(extraConstraints, getWhatsappTemplateConstraints(currentNode, subActionIndex, lang));
      }

      const payloadFields = _.get(
        template,
        `languageVariables.${lang}.variables`,
        [],
      );
      if (!_.isEmpty(payloadFields)) {
        Object.assign(
          extraConstraints,
          getNewActionSendPushLanguageConstraints(
            currentNode,
            subActionIndex,
            lang,
            payloadFields,
          ),
        );
      }
    });

    props.addExtraConstraints(extraConstraints);
  }

  getTemplateType() {
    const templateType = _.get(actionTypeToTemplateType, this.props.actionType, '');
    if (templateType) {
      return templateType;
    }
    // *Note: in case UCD created campaign, so the templateType is not determined by this.props.actionType.
    // Therefore, the templateType can be achieved from the template instance.
    const { actionMeta, templates } = this.props;
    const templateID = _.get(actionMeta, 'messagingPayload.templateID', '');
    const template = _.get(templates, `${templateID}`, {});
    return _.get(template, 'templateType');
  }

  render() {
    const { actionMeta, templates, targetUserType, actionType, user } = this.props;
    const templateID = _.get(actionMeta, 'messagingPayload.templateID', '');
    const templateName = _.get(actionMeta, 'messagingPayload.templateName', '');
    const templateType = this.getTemplateType();

    const params = _.get(actionMeta, 'messagingPayload.params');

    const isTemplateIDValid = _.isEmpty(_.get(actionMeta, 'error.templateID'));
    const isParamValid = _.isEmpty(_.get(actionMeta, 'error.messagingPayloadParams'));

    const template = _.get(templates, `${templateID}`, {});
    const audienceType = _.get(template, 'audienceType', '');
    const appName = _.get(template, 'appName', '');
    const category = _.get(template, 'category', '');
    const isEmptyAudienceType = (
      UCD_TARGET_AUDIENCES_ENABLED_TYPES[appName] &&
      UCD_TARGET_AUDIENCES_ENABLED_TYPES[appName].includes(templateType) &&
      category !== 'transaction' &&
      audienceType === ''
    );

    // actionType for trident will be single action e.g "sendSMS", "sendWhatsapp",
    // meanwhile for ucd guided campaign it will be the channelTypes and it could be more than one with comma separator. e.g= "sms,whatsapp" etc
    const isSendSMS = tridentCommActions.SMS.includes(actionType) || (actionType.indexOf('sms') !== -1);
    const isSendWhatsapp = tridentCommActions.Whatsapp.includes(this.props.actionType) || (actionType.indexOf('whatsapp') !== -1);
    const isSendLine = tridentCommActions.Line.includes(this.props.actionType) || (actionType.indexOf('line') !== -1);

    const isRequireBUToken = isSendSMS || isSendWhatsapp || isSendLine;
    const buToken = _.get(actionMeta, 'messagingPayload.buToken', '');


    const allowTestSend = !isSendSMS && !isSendWhatsapp && !isSendLine && isTemplateIDValid && (targetUserType === 'passenger' || targetUserType === 'driver');
    const userName = _.get(
      _.split(user.email, '@'), 0, ''
    );
    const templateUrl = `${window.Grab.config.HEDWIG_DASH_URI}/templates/create/${templateType}/?id=${templateID}`;
    const enableAudienceTypeValidation = _.get(this.props.featureFlagConfig, 'enableAudienceTypeValidation', false);
    const enforceAudienceTypeValidation = _.get(this.props.featureFlagConfig, 'enforceAudienceTypeValidation', false);

    return (
      <Form>
        {!isTemplateIDValid && (
          <Popup
            trigger={
              <Message negative size="tiny">
                <Message.Header>
                  <span className="validation-icon" />
                  Template ID not fetched / invalid.
                </Message.Header>
              </Message>
            }
            content={
              _.get(actionMeta, 'error.templateID') || 'enter valid templateID and fetch it.'
            }
          />
        )}
        {!isParamValid && (
          <Message negative size="tiny">
            <Message.Header>
              <span className="validation-icon" />
              Message params are missing
            </Message.Header>
          </Message>
        )}
        <Grid>
          <HedwigWidget
            targetUserType={targetUserType}
            actionType={actionType}
            templateName={templateName}
            user={userName}
            onSelect={this.handleTemplateSelect}
          />
          {
            !_.isEmpty(templateID) && (
              <Grid.Column width={16}>
                <p>Hedwig template URL: <a href={templateUrl} target="_blank">{templateID}</a></p>
              </Grid.Column>
            )
          }
          {!_.isEmpty(templateID) && !this.isFetching && enableAudienceTypeValidation && isEmptyAudienceType && (
            <Grid.Column width={16}>
              {/* Display warning message */}
              {!enforceAudienceTypeValidation && enableAudienceTypeValidation && (
                <Message size="tiny" style={{ backgroundColor: '#FFFACD' }}>
                  <Message.Header>
                    <Icon name="warning sign" color="yellow" size="large" className="validation-warning-icon" />
                    This template&apos;s Target Audience field is empty in Hedwig. Update it now to avoid being blocked in the future!
                  </Message.Header>
                </Message>
              )}
              {/* Display error message and block the form submission */}
              {enforceAudienceTypeValidation && enableAudienceTypeValidation && (
                <Message negative size="tiny" >
                  <Message.Header>
                    <span className="validation-icon" />
                    Selected template has &quot;Target Audience&quot; field empty in Hedwig. You can only add the action once you fill that field.
                  </Message.Header>
                </Message>
              )}
            </Grid.Column>
          )}
        </Grid>
        <p />

        {isRequireBUToken && !buToken && (
          <Popup
            trigger={
              <Message negative size="tiny">
                <Message.Header>
                  <span className="validation-icon" />
                  Business Unit Token is required
                </Message.Header>
              </Message>
            }
            content={
              _.get(actionMeta, 'error.buToken') || 'business unit token is required'
            }
          />
        )}
        {isRequireBUToken && (
          <Grid>
            <Grid.Column width={8}>
              <Form.Input
                required
                label="Business Unit Token"
                value={_.get(actionMeta, 'messagingPayload.buToken', '')}
                onChange={this.handleBusinessUnitToken}
              />
            </Grid.Column>
          </Grid>
        )}

        <ProgressiveContainer isLoading={this.isFetching}>
          <TemplateParamsForm
            templateInfo={template}
            templateParams={params}
            onChange={this.onTemplateParamsChange}
            error={this.props.error}
          />
        </ProgressiveContainer>

        {
          allowTestSend && (
            <Accordion>
              <Accordion.Title onClick={() => { this.setState({ showSendMessage: !this.state.showSendMessage }); }}>
                <Icon name="dropdown" />Test send message
              </Accordion.Title>
              <Accordion.Content active={this.state.showSendMessage} style={{ paddingLeft: '20px' }}>
                <SendPreviewMessageForm
                  templateID={templateID}
                  params={params}
                  userType={targetUserType}
                />
              </Accordion.Content>
            </Accordion>
          )
        }
      </Form>
    );
  }
}

export function mapStateToProps(state: StoreState) {
  return {
    templates: _.get(state, 'newTridents.templates', {}),
    fetchTemplateRequest: _.get(
      state.requests,
      'getTemplateTridentCampaignsRequest',
      {},
    ),
    user: state.app.user,
    featureFlagConfig: _.get(state, 'newTridents.featureFlagConfig', {}),
  };
}

export default connect(
  mapStateToProps,
  {
    getTemplate,
  },
  null,
  { forwardRef: true },
)(NewActionSendPushNotification);
